/**
 * The plan a guest builds and the quote the engine prices from it. The model never computes a price:
 * every number here comes from `quote/engine.ts` evaluating the current `PricingRule` version.
 */
import { z } from 'zod';
import { Cents, DateString, Id, Instant, NonNegativeCents, Percent, TimeHHmm } from './common.js';
/** `quantity` meaning depends on the add-on's pricing — see `AddonPricing`. */
export const AddonSelection = z.object({
    addonId: Id,
    quantity: z.number().int().min(1).max(100),
});
/**
 * What the guest has said so far. Every field is optional: the start scene may give only an occasion.
 * In a PATCH, an omitted field is unchanged and `null` clears it.
 */
export const PlanInput = z.object({
    eventTypeId: Id.nullish(),
    /** e.g. "Maya's 40th". May contain a child's name: PII, never logged. */
    eventName: z.string().trim().max(200).nullish(),
    occasionNotes: z.string().max(4000).nullish(),
    /** The headcount priced and checked against capacity. */
    guestCount: z.number().int().min(1).max(2000).nullish(),
    /** Upper bound when the guest gave a range ("60–70"); informational. */
    guestCountMax: z.number().int().min(1).max(2000).nullish(),
    date: DateString.nullish(),
    dateFlexible: z.boolean().nullish(),
    /** e.g. "a Sunday in March". */
    dateFlexibleText: z.string().max(200).nullish(),
    start: TimeHHmm.nullish(),
    durationMinutes: z.number().int().min(30).max(1440).nullish(),
    productId: Id.nullish(),
    packageId: Id.nullish(),
    addons: z.array(AddonSelection).max(50).nullish(),
    dietaryNotes: z.string().max(2000).nullish(),
});
/** The plan as stored on a lead and returned: every key present, unknowns are `null`. */
export const Plan = z.object({
    eventTypeId: Id.nullable(),
    eventName: z.string().nullable(),
    occasionNotes: z.string().nullable(),
    guestCount: z.number().int().nullable(),
    guestCountMax: z.number().int().nullable(),
    date: DateString.nullable(),
    dateFlexible: z.boolean(),
    dateFlexibleText: z.string().nullable(),
    start: TimeHHmm.nullable(),
    durationMinutes: z.number().int().nullable(),
    productId: Id.nullable(),
    packageId: Id.nullable(),
    addons: z.array(AddonSelection),
    dietaryNotes: z.string().nullable(),
});
export const EMPTY_PLAN = {
    eventTypeId: null,
    eventName: null,
    occasionNotes: null,
    guestCount: null,
    guestCountMax: null,
    date: null,
    dateFlexible: false,
    dateFlexibleText: null,
    start: null,
    durationMinutes: null,
    productId: null,
    packageId: null,
    addons: [],
    dietaryNotes: null,
};
/** What the plan still lacks before the quote is complete. */
export const QuoteMissing = z.enum(['guestCount', 'product', 'package', 'date']);
export const QuoteLineKind = z.enum([
    'package',
    'addon',
    'min_spend_shortfall',
    'service_charge',
    'tax',
    'card_fee',
]);
/** One line, rounded half-up to the cent on its own. */
export const QuoteLine = z.object({
    kind: QuoteLineKind,
    /** Display label from content (package/add-on name, pricing-rule labels). */
    label: z.string(),
    /** The package or add-on id for those kinds. */
    refId: Id.nullable(),
    quantity: z.number().nullable(),
    unitCents: Cents.nullable(),
    amountCents: Cents,
});
export const BalanceInstallment = z.object({
    label: z.string(),
    daysBefore: z.number().int(),
    /** Null when the plan has no date yet. */
    dueDate: DateString.nullable(),
    amountCents: NonNegativeCents,
});
/**
 * One part of what is due now. `deposit` is the pricing rule's deposit; `folded_installment` is a
 * balance installment whose due date had passed when the quote (or Checkout) was made, so it is paid
 * with the deposit. `label` is the deposit's ("25% deposit") or the installment's pricing-rule label.
 */
export const DepositComponentKind = z.enum(['deposit', 'folded_installment']);
export const DepositComponent = z.object({
    kind: DepositComponentKind,
    label: z.string(),
    amountCents: NonNegativeCents,
    /** When a folded installment was due (venue-local); null for the deposit itself. */
    originalDueDate: DateString.nullable(),
    /** A folded installment's days before the event (pricing rule); null for the deposit itself. */
    daysBefore: z.number().int().nullable(),
});
/** The deposit as one component (also for a quote or booking stored before components existed). */
export const plainDeposit = (amountCents, label = 'Deposit') => ({
    kind: 'deposit',
    label,
    amountCents,
    originalDueDate: null,
    daysBefore: null,
});
/** What is due now, part by part: the stored components, else the deposit as one part. */
export function depositComponentsOf(q) {
    return q.depositComponents?.length ? q.depositComponents : [plainDeposit(q.depositDueCents)];
}
/** The installments folded into the deposit (empty when none was). */
export const foldedInstallments = (components) => components.filter((c) => c.kind === 'folded_installment');
/**
 * A quote never presents a payment date in the past: an installment due before `today` (venue-local)
 * is folded into the deposit, which is due now, at signing. An installment due today stays as it is.
 * Every quote engine (the API's and the fakes that stand in for it) applies this one rule.
 *
 * `deposit` is the rule's deposit, or the components an earlier fold left (Checkout folds the stored
 * quote again on its own day). Each folded installment becomes a component, so every surface can say
 * why the amount due now is more than the deposit; `depositDueCents` is always their sum.
 */
export function foldPastDueInstallments(deposit, schedule, today) {
    const depositComponents = typeof deposit === 'number' ? [plainDeposit(deposit)] : [...deposit];
    const balanceSchedule = [];
    for (const b of schedule) {
        if (b.dueDate !== null && b.dueDate < today)
            depositComponents.push({
                kind: 'folded_installment',
                label: b.label,
                amountCents: b.amountCents,
                originalDueDate: b.dueDate,
                daysBefore: b.daysBefore,
            });
        else
            balanceSchedule.push(b);
    }
    const depositDueCents = depositComponents.reduce((n, c) => n + c.amountCents, 0);
    return { depositDueCents, depositComponents, balanceSchedule };
}
/** Shown separately and only when the pricing rule enables it; not included in `totalCents`. */
export const CardFee = z.object({
    label: z.string(),
    pct: Percent,
    achExempt: z.boolean(),
    /** The fee on the deposit and on the total if paid by card. */
    depositFeeCents: NonNegativeCents,
    totalFeeCents: NonNegativeCents,
});
export const Quote = z.object({
    /** Null when not persisted (priced without a lead token). */
    id: Id.nullable(),
    leadId: Id.nullable(),
    pricingRuleId: Id,
    pricingRuleVersion: z.number().int().min(1),
    guestCount: z.number().int().nullable(),
    productId: Id.nullable(),
    packageId: Id.nullable(),
    date: DateString.nullable(),
    addons: z.array(AddonSelection),
    lines: z.array(QuoteLine),
    /** Package + add-ons. */
    subtotalCents: NonNegativeCents,
    minSpendShortfallCents: NonNegativeCents,
    serviceChargeCents: NonNegativeCents,
    taxCents: NonNegativeCents,
    totalCents: NonNegativeCents,
    /** Due now, at signing: the deposit plus any folded installments (the sum of `depositComponents`). */
    depositDueCents: NonNegativeCents,
    /**
     * `depositDueCents` part by part: the deposit, then each folded installment. Absent on quotes stored
     * before it existed; read it with `depositComponentsOf`.
     */
    depositComponents: z.array(DepositComponent).optional(),
    balanceSchedule: z.array(BalanceInstallment),
    cardFee: CardFee.nullable(),
    validUntil: Instant.nullable(),
    /** Empty when the quote is complete. */
    missing: z.array(QuoteMissing),
    createdAt: Instant.nullable(),
});
//# sourceMappingURL=quote.js.map